import { ChevronDown, FileWarning } from "lucide-react";
import type { ExportIssue } from "@lib/roundExport/contract";

const groups = [
  {
    key: "access",
    title: "Needs access",
    categories: ["accessDenied"],
    help: "The exporter could not access these files. Linked documents are downloaded without your browser’s sign-in. Check the link and its sharing settings.",
  },
  {
    key: "missing",
    title: "Not found",
    categories: ["notFound"],
    help: "These files may have moved or been deleted. Check the original application links.",
  },
  {
    key: "temporary",
    title: "Couldn’t download",
    categories: ["rateLimited", "timedOut", "sourceBusy"],
    help: "The source was rate-limited, timed out, or temporarily unavailable. You can prepare a new snapshot later.",
  },
  {
    key: "unsupported",
    title: "Unsupported or too large",
    categories: ["unsupported", "tooLarge"],
    help: "These files could not be captured within the export’s download limits.",
  },
  {
    key: "other",
    title: "Other missing material",
    categories: ["otherFailures"],
    help: "See the reason for each item below. Original references remain in the export.",
  },
];

// Older snapshots retain HTTP status in their reason but predate categories.
// Only classify explicit evidence; never infer permissions from a generic failure.
function issueCategory(
  issue: ExportIssue,
): NonNullable<ExportIssue["category"]> {
  if (issue.category) {
    return issue.category;
  }

  const status = issue.reason.match(/HTTP (\d{3})\b/)?.[1];

  if (
    status === "401" ||
    status === "403" ||
    issue.reason === "Google document requires access"
  ) {
    return "accessDenied";
  }

  if (status === "404" || status === "410") {
    return "notFound";
  }

  if (status === "429") {
    return "rateLimited";
  }

  if (status && ["500", "502", "503", "504"].includes(status)) {
    return "sourceBusy";
  }

  return "otherFailures";
}

function sourceLink(source: string): string | undefined {
  try {
    const url = new URL(source);

    if (
      ["https:", "http:"].includes(url.protocol) &&
      !url.username &&
      !url.password
    ) {
      return url.href;
    }
  } catch {
    // Some sources are application field names, rather than URLs.
  }

  return undefined;
}

export function ExportIssues({ issues }: { issues: readonly ExportIssue[] }) {
  if (!issues.length) {
    return null;
  }

  const warningsOnly = issues.every((issue) => issue.severity === "warning");

  return (
    <section className="mt-4 flex flex-col gap-3" aria-label="Missing material">
      {!warningsOnly && (
        <div>
          <p className="font-semibold">
            {issues.length} items weren’t included
          </p>
          <p className="body-sm text-text-secondary">
            You can download the snapshot now. The same missing-material report
            is included in the ZIP.
          </p>
        </div>
      )}
      <details className="group rounded-lg border border-border-subtle">
        <summary className="flex cursor-pointer list-none items-center gap-3 rounded-lg px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
          <FileWarning
            className="size-4 shrink-0 text-text-secondary"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">
              {warningsOnly
                ? `${issues.length} attachment ${issues.length === 1 ? "link" : "links"} unavailable`
                : "Missing files"}
            </span>
            <span className="block body-sm text-text-secondary">
              {groups
                .map((group) => {
                  const count = issues.filter((issue) =>
                    group.categories.includes(issueCategory(issue)),
                  ).length;
                  return count ? `${group.title} (${count})` : null;
                })
                .filter(Boolean)
                .join(" • ")}
            </span>
          </span>
          <ChevronDown
            className="size-4 shrink-0 text-text-secondary group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="max-h-[min(14rem,30vh)] overflow-y-auto border-t border-border-subtle px-4 divide-y divide-border-subtle">
          {groups.map((group) => {
            const items = issues.filter((issue) =>
              group.categories.includes(issueCategory(issue)),
            );

            if (!items.length) {
              return null;
            }

            return (
              <section
                key={group.key}
                className="py-4"
                aria-label={group.title}
              >
                <h3 className="body-sm font-semibold">
                  {group.title} ({items.length})
                </h3>
                <p className="mt-2 body-sm text-text-secondary">{group.help}</p>
                <ul className="mt-3 divide-y divide-border-subtle">
                  {items.map((issue, index) => {
                    const href = sourceLink(issue.source);
                    return (
                      <li
                        key={`${issue.source}:${index}`}
                        className="min-w-0 py-3 first:pt-0 last:pb-0 body-sm"
                      >
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="break-all underline underline-offset-2"
                          >
                            {issue.source}
                          </a>
                        ) : (
                          <p className="break-words font-medium">
                            {issue.source}
                          </p>
                        )}
                        <p className="mt-1 text-text-secondary">
                          {issue.reason}
                          {issue.attempts != null && issue.attempts > 1
                            ? ` Tried ${issue.attempts} times.`
                            : ""}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </details>
    </section>
  );
}
